import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Plus, User, Edit2, Trash2,
  History, ChevronLeft, ChevronRight, X, Loader2,
  CheckCircle
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import EmployeSelector from './shared/EmployeSelector';
import PeriodSelector from './shared/PeriodSelector';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

const TYPES_PRIME = {
  performance:    { label: 'Performance',     color: '#2563eb', bg: '#eff6ff' },
  assiduite:      { label: 'Assiduité',       color: '#16a34a', bg: '#f0fdf4' },
  anciennete:     { label: 'Ancienneté',      color: '#7c3aed', bg: '#f5f3ff' },
  exceptionnelle: { label: 'Exceptionnelle',  color: '#dc2626', bg: '#fef2f2' },
  fin_annee:      { label: 'Fin d\'année',    color: '#d97706', bg: '#fffbeb' },
  autre:          { label: 'Autre',           color: '#64748b', bg: '#f8fafc' },
};

const MOIS_NOMS = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre'
];

const PrimesModule = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employes, setEmployes] = useState([]);
  const [selectedEmploye, setSelectedEmploye] = useState('');
  const [loadingEmployes, setLoadingEmployes] = useState(false);
  const [searchTermEmploye, setSearchTermEmploye] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Primes du mois
  const [primesMois, setPrimesMois] = useState([]);
  const [totalPrimesMois, setTotalPrimesMois] = useState(0);

  // Historique
  const [historiquePrimes, setHistoriquePrimes] = useState([]);
  const [currentPageHistorique, setCurrentPageHistorique] = useState(1);
  const [itemsPerPageHistorique] = useState(3);

  // Modal création
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [montantPrime, setMontantPrime] = useState('');
  const [typePrime, setTypePrime] = useState('performance');
  const [motifPrime, setMotifPrime] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Modal édition
  const [showEditModal, setShowEditModal] = useState(false);
  const [primeToEdit, setPrimeToEdit] = useState(null);
  const [montantEdit, setMontantEdit] = useState('');
  const [typeEdit, setTypeEdit] = useState('performance');
  const [motifEdit, setMotifEdit] = useState('');
  const [justificationEdit, setJustificationEdit] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Suppression
  const [confirmDialogConfig, setConfirmDialogConfig] = useState(null);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole } = useParamsCompta();

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => {
    loadEmployesData();
  }, [selectedDate]);

  useEffect(() => {
    if (selectedEmploye) {
      loadPrimesMois(selectedEmploye);
      loadHistoriquePrimes(selectedEmploye);
      setCurrentPageHistorique(1);
    }
  }, [selectedEmploye, selectedDate]);

  const loadEmployesData = async () => {
    showLoader();
    setLoadingEmployes(true);
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const response = await api.comptabilite.getPointagesMensuelsEmployes(mois, annee);
      if (response.success) setEmployes(response.data);
      else showToast(response.message || 'Erreur lors du chargement', 'error');
    } catch (error) {
      showToast('Erreur lors du chargement des employés', 'error');
    } finally {
      setLoadingEmployes(false);
      hideLoader();
    }
  };

  const loadPrimesMois = async (employeId) => {
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const response = await api.primes.getByEmployeMois(employeId, mois, annee);
      if (response.success) {
        setPrimesMois(response.data.primes || []);
        setTotalPrimesMois(response.data.total_primes || 0);
      }
    } catch (error) {
      console.error('Erreur chargement primes:', error);
    }
  };

  const loadHistoriquePrimes = async (employeId) => {
    try {
      const response = await api.primes.getByEmploye(employeId);
      if (response.success) setHistoriquePrimes(response.data);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  };

  // ============================================================================
  // CRÉATION
  // ============================================================================
  const handleCreatePrime = async () => {
    if (!montantPrime || parseFloat(montantPrime) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.primes.creer({
        employe_id: parseInt(selectedEmploye),
        mois: selectedDate.getMonth() + 1,
        annee: selectedDate.getFullYear(),
        montant: parseFloat(montantPrime),
        type_prime: typePrime,
        motif: motifPrime || null,
      });

      if (response.success) {
        showToast('Prime attribuée avec succès', 'success');
        setShowCreateModal(false);
        setMontantPrime('');
        setTypePrime('performance');
        setMotifPrime('');
        loadPrimesMois(selectedEmploye);
        loadHistoriquePrimes(selectedEmploye);
      } else {
        showToast(response.message || 'Erreur lors de la création', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Erreur lors de la création', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  // ============================================================================
  // MODIFICATION
  // ============================================================================
  const handleEditPrime = async () => {
    if (!justificationEdit.trim()) {
      showToast('Veuillez justifier la modification', 'error');
      return;
    }
    if (!montantEdit || parseFloat(montantEdit) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    setIsEditing(true);
    try {
      const response = await api.primes.modifier(primeToEdit.id, {
        montant: parseFloat(montantEdit),
        type_prime: typeEdit,
        motif: motifEdit,
        justification_modification: justificationEdit,
      });

      if (response.success) {
        showToast('Prime modifiée avec succès', 'success');
        setShowEditModal(false);
        loadPrimesMois(selectedEmploye);
        loadHistoriquePrimes(selectedEmploye);
      } else {
        showToast(response.message || 'Erreur', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Erreur lors de la modification', 'error');
    } finally {
      setIsEditing(false);
    }
  };

  const employeActuel = employes.find(e => e.id.toString() === selectedEmploye);

  // Pagination historique
  const indexOfLastItem = currentPageHistorique * itemsPerPageHistorique;
  const indexOfFirstItem = indexOfLastItem - itemsPerPageHistorique;
  const currentHistoriqueItems = historiquePrimes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPagesHistorique = Math.ceil(historiquePrimes.length / itemsPerPageHistorique);

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>

      {/* Sélecteur de période */}
      <PeriodSelector selectedDate={selectedDate} onChange={setSelectedDate} />

      {/* Grille principale */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Colonne 1 : Liste employés */}
        <div className="lg:col-span-3">
          <EmployeSelector
            employes={employes}
            selectedEmploye={selectedEmploye}
            onSelect={(emp) => setSelectedEmploye(emp.id.toString())}
            searchTerm={searchTermEmploye}
            onSearchChange={setSearchTermEmploye}
            loading={loadingEmployes}
            color="primary"
            renderBadge={(employe) => null}
          />
        </div>

        {/* Colonne 2 : Gestion des primes */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-6">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #4ade80 100%)' }}>
            <TrendingUp size={24} className="text-white" />
            <h2 className="font-bold text-white text-lg">Gestion des primes</h2>
          </div>

          {!selectedEmploye ? (
            <div className="text-center py-16 text-gray-400">
              <TrendingUp className="h-20 w-20 mx-auto mb-4 opacity-20" />
              <h4 className="text-lg font-bold text-gray-700 mb-2">Aucun employé sélectionné</h4>
              <p className="text-sm font-medium">Sélectionnez un employé dans la liste</p>
            </div>
          ) : (
            <div className="space-y-6">

              {/* Info employé */}
              <div className="p-4 rounded-xl border-2"
                style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900">
                      {employeActuel?.prenom} {employeActuel?.nom}
                    </div>
                    <div className="text-sm font-medium text-gray-600 mt-1">
                      {employeActuel?.role} — {employeActuel?.departement || 'N/A'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">
                      + {formatMontant(totalPrimesMois)}
                    </div>
                    <div className="text-xs font-medium text-gray-600">Total primes du mois</div>
                  </div>
                </div>
              </div>

              {/* Bouton créer */}
              <button
                onClick={() => {
                  setMontantPrime('');
                  setTypePrime('performance');
                  setMotifPrime('');
                  setShowCreateModal(true);
                }}
                className="w-full py-3 px-4 text-white rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#16a34a' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Nouvelle prime
              </button>

              {/* Liste primes du mois */}
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                {primesMois.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p className="text-sm font-medium">Aucune prime ce mois</p>
                  </div>
                ) : (
                  <>
                    {primesMois.map((prime) => {
                      const typeInfo = TYPES_PRIME[prime.type_prime] || TYPES_PRIME.autre;
                      return (
                        <div key={prime.id}
                          className="p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="px-2.5 py-1 rounded-lg text-xs font-semibold"
                              style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
                              {typeInfo.label}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-3 py-1 font-semibold rounded-lg ${
                                prime.statut === 'appliquee'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {prime.statut === 'appliquee' ? 'Appliquée' : 'Validée'}
                              </span>
                            </div>
                          </div>

                          <div className="text-xl font-bold text-green-600 mb-2">
                            + {formatMontant(prime.montant)}
                          </div>

                          {prime.motif && (
                            <div className="text-sm font-medium text-gray-700 mb-2">{prime.motif}</div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <div className="text-xs font-medium text-gray-600">
                                {new Date(prime.date_attribution).toLocaleDateString('fr-FR')}
                              </div>
                              {prime.created_by_name && (
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <User size={12} />
                                  <span>Par : {prime.created_by_name}</span>
                                </div>
                              )}
                            </div>

                            {prime.statut === 'validee' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setPrimeToEdit(prime);
                                    setMontantEdit(prime.montant.toString());
                                    setTypeEdit(prime.type_prime);
                                    setMotifEdit(prime.motif || '');
                                    setJustificationEdit('');
                                    setShowEditModal(true);
                                  }}
                                  className="p-2 rounded-lg hover:bg-blue-50 transition-colors">
                                  <Edit2 className="w-4 h-4" style={{ color: '#448696' }} />
                                </button>
                                <button
                                  onClick={() => setConfirmDialogConfig({ primeId: prime.id, montant: prime.montant })}
                                  className="p-2 rounded-lg hover:bg-red-50 transition-colors">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Total */}
                    <div className="p-4" style={{ backgroundColor: '#f0fdf4' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-700">Total primes du mois</span>
                        <span className="text-2xl font-bold text-green-600">
                          + {formatMontant(totalPrimesMois)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Colonne 3 : Historique */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3"
            style={{ backgroundColor: '#448696' }}>
            <History size={24} className="text-white" />
            <h2 className="font-bold text-white text-lg">Historique</h2>
          </div>

          {!selectedEmploye ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-xs font-medium">Sélectionnez un employé</p>
            </div>
          ) : historiquePrimes.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-xs font-medium">Aucune prime</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {currentHistoriqueItems.map((prime) => {
                  const typeInfo = TYPES_PRIME[prime.type_prime] || TYPES_PRIME.autre;
                  return (
                    <div key={prime.id}
                      className={`p-3 rounded-xl border transition-colors ${
                        prime.statut === 'annulee'
                          ? 'border-red-200 bg-red-50 opacity-70'
                          : 'border-gray-100 hover:bg-gray-50'
                      }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold text-gray-900">
                          {MOIS_NOMS[prime.mois - 1]} {prime.annee}
                        </div>
                        <span className={`text-xs px-2 py-1 font-semibold rounded-lg ${
                          prime.statut === 'validee' ? 'bg-blue-100 text-blue-700'
                          : prime.statut === 'appliquee' ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                        }`}>
                          {prime.statut === 'validee' ? 'Validée'
                            : prime.statut === 'appliquee' ? 'Appliquée'
                            : 'Annulée'}
                        </span>
                      </div>

                      <span className="px-2 py-0.5 rounded-md text-xs font-semibold mb-2 inline-block"
                        style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}>
                        {typeInfo.label}
                      </span>

                      <div className={`text-lg font-bold mb-2 ${prime.statut === 'annulee' ? 'line-through' : ''}`}
                        style={{ color: prime.statut === 'annulee' ? '#9ca3af' : '#16a34a' }}>
                        + {formatMontant(prime.montant)}
                      </div>

                      {prime.motif && (
                        <div className="text-xs text-gray-500 italic mb-1">"{prime.motif}"</div>
                      )}

                      <div className="space-y-1">
                        <div className="text-xs font-medium text-gray-600">
                          Attribuée le {new Date(prime.date_attribution).toLocaleDateString('fr-FR')}
                        </div>
                        {prime.created_by_name && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <User size={12} /><span>Par : {prime.created_by_name}</span>
                          </div>
                        )}
                        {prime.statut === 'annulee' && (
                          <>
                            {prime.date_suppression && (
                              <div className="flex items-center gap-1 text-xs text-red-600 mt-2">
                                <X size={12} />
                                <span>Annulée le {new Date(prime.date_suppression).toLocaleDateString('fr-FR')}</span>
                              </div>
                            )}
                            {prime.justification_suppression && (
                              <div className="text-xs italic text-red-700 mt-1 p-2 bg-red-100 rounded border border-red-200">
                                "{prime.justification_suppression}"
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPagesHistorique > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-600">
                    Page {currentPageHistorique} sur {totalPagesHistorique}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPageHistorique(p => Math.max(p - 1, 1))}
                      disabled={currentPageHistorique === 1}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentPageHistorique(p => Math.min(p + 1, totalPagesHistorique))}
                      disabled={currentPageHistorique === totalPagesHistorique}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ================================================================== */}
      {/* MODALES                                                             */}
      {/* ================================================================== */}

      {/* Modal Création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #4ade80 100%)' }}>
              <Plus size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Nouvelle prime</h3>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type de prime *</label>
              <select value={typePrime} onChange={e => setTypePrime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={e => e.target.style.borderColor = '#16a34a'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}>
                {Object.entries(TYPES_PRIME).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Montant ({symbole}) *
              </label>
              <input type="number" min="0" value={montantPrime}
                onChange={e => setMontantPrime(e.target.value)}
                placeholder="Ex: 50 000"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={e => { e.target.style.borderColor = '#16a34a'; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
              <textarea value={motifPrime} onChange={e => setMotifPrime(e.target.value)} rows="3"
                placeholder="Ex: Excellent résultat sur le projet X..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all resize-none"
                onFocus={e => { e.target.style.borderColor = '#16a34a'; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
                Annuler
              </button>
              <button onClick={handleCreatePrime} disabled={isCreating}
                className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                style={{ backgroundColor: '#16a34a' }}
                onMouseEnter={e => !isCreating && (e.currentTarget.style.backgroundColor = '#15803d')}
                onMouseLeave={e => !isCreating && (e.currentTarget.style.backgroundColor = '#16a34a')}>
                {isCreating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Attribuer la prime'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Édition */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #4ade80 100%)' }}>
              <Edit2 size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Modifier la prime</h3>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type de prime *</label>
              <select value={typeEdit} onChange={e => setTypeEdit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={e => e.target.style.borderColor = '#16a34a'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}>
                {Object.entries(TYPES_PRIME).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Montant *</label>
              <input type="number" min="0" value={montantEdit}
                onChange={e => setMontantEdit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={e => { e.target.style.borderColor = '#16a34a'; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
              <textarea value={motifEdit} onChange={e => setMotifEdit(e.target.value)} rows="2"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all resize-none"
                onFocus={e => { e.target.style.borderColor = '#16a34a'; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Justification de la modification *</label>
              <textarea value={justificationEdit} onChange={e => setJustificationEdit(e.target.value)} rows="3"
                placeholder="Pourquoi modifiez-vous cette prime ?"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all resize-none"
                onFocus={e => { e.target.style.borderColor = '#16a34a'; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
                Annuler
              </button>
              <button onClick={handleEditPrime} disabled={isEditing}
                className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                style={{ backgroundColor: '#16a34a' }}
                onMouseEnter={e => !isEditing && (e.currentTarget.style.backgroundColor = '#15803d')}
                onMouseLeave={e => !isEditing && (e.currentTarget.style.backgroundColor = '#16a34a')}>
                {isEditing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Modifier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ConfirmDialog suppression */}
      {confirmDialogConfig && (
        <ConfirmDialog
          isOpen={true}
          title="Annuler cette prime"
          message={`Confirmer l'annulation de la prime de ${formatMontant(confirmDialogConfig.montant)} ?`}
          type="warning"
          confirmText="Annuler la prime"
          cancelText="Retour"
          requireJustification={true}
          onConfirm={async (justification) => {
            try {
              const response = await api.primes.annuler(confirmDialogConfig.primeId, justification);
              if (response.success) {
                showToast('Prime annulée avec succès', 'success');
                await loadPrimesMois(selectedEmploye);
                await loadHistoriquePrimes(selectedEmploye);
                setConfirmDialogConfig(null);
                return true;
              } else {
                showToast(response.message || 'Erreur', 'error');
                return false;
              }
            } catch (error) {
              showToast(error.message || 'Erreur lors de l\'annulation', 'error');
              return false;
            }
          }}
          onCancel={() => setConfirmDialogConfig(null)}
        />
      )}

      {/* Toast */}
      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}
    </div>
  );
};

export default PrimesModule;